import PageLoadedQueue from "../PageLoadedQueue";
import CheckoutItem from "./CheckoutItem";
import {RenderModal} from "../Functions/RenderModal";
import {ShoppingList} from "./ShoppingList";
import QuickOrder from "./QuickOrder";

class Checkout {
    private readonly checkoutList: HTMLElement;
    private readonly listIdentifier: string;
    private readonly removeAllButton: HTMLElement|undefined;
    private readonly removeInitButton: HTMLElement|undefined;
    private readonly removeConfirmButton: HTMLElement|undefined;
    private readonly removeAbortButton: HTMLElement|undefined;
    private isDeletingMode: boolean = false;

    constructor(
        checkoutList: HTMLElement,
        listIdentifier: string,
        removeAllButton: HTMLElement|undefined,
        removeInitButton: HTMLElement|undefined,
        removeConfirmButton: HTMLElement|undefined,
        removeAbortButton: HTMLElement|undefined,
    ) {
        this.checkoutList = checkoutList;
        this.listIdentifier = listIdentifier;
        this.removeAllButton = removeAllButton;
        this.removeInitButton = removeInitButton;
        this.removeConfirmButton = removeConfirmButton;
        this.removeAbortButton = removeAbortButton;

        this.initListener();
    }

    /**
     * Initialize event listeners
     */
    protected initListener = (): void => {
        this.toggleDeletion(this.isDeletingMode);

        if (this.removeAllButton) {
            this.removeAllButton.addEventListener(
                'click',
                this.confirmRemoveAll.bind(this)
            );
        }

        if (this.removeInitButton) {
            this.removeInitButton.addEventListener(
                'click',
                this.initialRemoveSelection.bind(this)
            );
        }

        if (this.removeConfirmButton) {
            this.removeConfirmButton.addEventListener(
                'click',
                this.confirmRemoveSelection.bind(this)
            );
        }

        if (this.removeAbortButton) {
            this.removeAbortButton.addEventListener(
                'click',
                this.abortRemoveSelection.bind(this)
            );
        }

        // Remove single items directly
        this.checkoutList.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            const checkoutItemRemoveButton = target.closest('[data-plandent-checkout-remove-item]');

            if (checkoutItemRemoveButton) {
                const checkoutItemElement: HTMLElement = checkoutItemRemoveButton.closest('[data-plandent-checkout-item]');

                const checkoutItem = CheckoutItem.createInstanceFromCheckoutRow(checkoutItemElement);
                checkoutItem.removeItemFromCart();
            }
        });

        document.addEventListener(QuickOrder.QUICK_ORDER_EVENT, () => {
            this.toggleDeletion(false);
        }, {passive: true});
    }

    /**
     * Initial remove selection
     */
    protected initialRemoveSelection = (): void => {
        this.toggleDeletion(true);
    }

    /**
     * Confirm remove selection
     */
    protected confirmRemoveSelection = (): void => {
        this.toggleDeletion(false);

        const selectedItems = this.getSelectedItemsForRemoval();
        if (selectedItems.length > 0) {
            this.confirmationModal(selectedItems);
        }

        this.toggleDeletion(false);
    }

    /**
     * Remove all items from the list
     */
    protected confirmRemoveAll = (): void => {
        if (!this.removeAllButton) {
            return;
        }

        const list = ShoppingList.getShoppingList(this.removeAllButton);

        this.confirmationModal('all', list);
        this.toggleDeletion(false);
    }

    /**
     * Confirmation modal
     *
     * @param items
     * @param list
     */
    protected confirmationModal = (items: HTMLElement[]|string, list: HTMLElement|undefined = undefined) => {
        const confirmationModal = document.querySelector('.confirmationmodal');
        if (!confirmationModal) {
            return;
        }

        const confirmDelete = confirmationModal.querySelector('.confirm-delete').textContent;
        const cancelDelete = confirmationModal.querySelector('.confirm-cancel').textContent;
        const content = confirmationModal.innerHTML;

        RenderModal.init(
            '',
            content,
            {
                confirm: {
                    label: confirmDelete,
                    className: 'btn-primary',
                    callback: () => {
                        ShoppingList.removeItemsFromShoppingList(items, list);
                    }
                },
                cancel: {
                    label: cancelDelete,
                    className: 'btn-default',
                    callback: () => {
                        this.abortRemoveSelection();
                    }
                }
            },
            false,
            'small'
        )
    }

    /**
     * Abort remove selection
     */
    protected abortRemoveSelection = (): void => {
        this.toggleDeletion(false);

        this.getCheckoutItems().forEach((item) => {
            item.uncheckCheckbox();
        });
    }

    /**
     * Toggle deletion mode
     *
     * @param deletingMode
     */
    protected toggleDeletion = (deletingMode: boolean = false): void => {
        this.isDeletingMode = deletingMode;

        if (this.removeInitButton) {
            this.removeInitButton.parentElement.style.display = this.isDeletingMode ? 'none' : 'block';
            this.removeConfirmButton.parentElement.style.display = this.isDeletingMode ? 'block' : 'none';
        }

        if (this.removeAbortButton) {
            this.removeAbortButton.parentElement.style.display = this.isDeletingMode ? 'block' : 'none';
        }

        if (this.removeAllButton) {
            if (this.isDeletingMode) {
                this.removeAllButton.closest('.dropdown').querySelector('.dropdown-toggle').classList.add('active');
            } else {
                this.removeAllButton.closest('.dropdown').querySelector('.dropdown-toggle').classList.remove('active');
            }
        }

        this.getCheckoutItems().forEach((item) => {
            item.showCheckbox(this.isDeletingMode);
        });
    }

    /**
     * Get checkout items
     */
    protected getCheckoutItems = (): CheckoutItem[] => {
        let checkoutItems: CheckoutItem[] = [];

        const newItems: NodeListOf<HTMLElement> = this.checkoutList.querySelectorAll('[data-plandent-checkout-item]');
        newItems.forEach((item) => {
            const checkoutItem = CheckoutItem.createInstanceFromCheckoutRow(item);

            if (checkoutItem) {
                checkoutItems.push(checkoutItem);
            }
        });

        return checkoutItems;
    }

    /**
     * Get selected items for removal
     */
    protected getSelectedItemsForRemoval = (): HTMLElement[] => {
        const selectedItems: HTMLElement[] = [];

        this.getCheckoutItems().forEach((item) => {
            if (item.isChecked()) {
                selectedItems.push(item.checkoutRow);
            }
        });

        return selectedItems;
    }

    /**
     * Initialize checkout
     */
    static init = () => {
        const checkoutList: NodeListOf<HTMLElement> = document.querySelectorAll('.avency-nwd-shoppinglists-shoppingcart');

        checkoutList.forEach((list) => {
            const listIdentifier = list.dataset.listId;
            const removeAllButton: HTMLElement|undefined = list.querySelector('[data-plandent-checkout-remove-selected="all"]');
            const removeInitButton: HTMLElement|undefined = list.querySelector('[data-plandent-checkout-remove-selected="init"]');
            const removeConfirmButton: HTMLElement|undefined = list.querySelector('[data-plandent-checkout-remove-selected="confirm"]');
            const removeAbortButton: HTMLElement|undefined = list.querySelector('[data-plandent-checkout-remove-selected="abort"]');

            new Checkout(list, listIdentifier, removeAllButton, removeInitButton, removeConfirmButton, removeAbortButton);
        });
    }
}

PageLoadedQueue.addFunction(Checkout.init);
